import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { batchAPI } from '../api';
import './BatchWorkflow.css';

function BatchWorkflow() {
  const navigate = useNavigate();
  const [urls, setUrls] = useState(['']);
  const [languages, setLanguages] = useState([]);
  const [batchName, setBatchName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const availableLanguages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'de-DE', name: 'German' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'fr-FR', name: 'French' },
  ];

  const handleAddUrl = () => {
    setUrls([...urls, '']);
  };

  const handleRemoveUrl = (index) => {
    const newUrls = urls.filter((_, i) => i !== index);
    setUrls(newUrls.length > 0 ? newUrls : ['']);
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const handleLanguageToggle = (langCode) => {
    if (languages.includes(langCode)) {
      setLanguages(languages.filter(l => l !== langCode));
    } else {
      setLanguages([...languages, langCode]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate URLs
    const validUrls = urls.filter(url => url.trim() !== '');
    if (validUrls.length === 0) {
      setError('Please enter at least one URL');
      return;
    }

    // Validate languages
    if (languages.length === 0) {
      setError('Please select at least one language');
      return;
    }

    // Validate batch name
    if (!batchName.trim()) {
      setError('Please enter a batch name');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await batchAPI.createBatchJob(validUrls, languages, batchName);
      navigate(`/batch/${result.batch_job_id}`);
    } catch (error) {
      console.error('Failed to create batch job:', error);
      setError(error.response?.data?.error || 'Failed to create batch job');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="batch-workflow">
      <h1>Batch Translation Workflow</h1>
      <p className="description">
        Process multiple Amazon product URLs and translate them to multiple languages in one batch.
      </p>

      <form onSubmit={handleSubmit} className="batch-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-section">
          <label htmlFor="batch-name">Batch Name</label>
          <input
            id="batch-name"
            type="text"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="e.g., Holiday Season Products"
            className="batch-name-input"
            disabled={isSubmitting}
          />
        </div>

        <div className="form-section">
          <label>Product URLs</label>
          <div className="urls-list">
            {urls.map((url, index) => (
              <div key={index} className="url-input-group">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                  placeholder="https://www.amazon.fr/..."
                  className="url-input"
                  disabled={isSubmitting}
                />
                {urls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(index)}
                    className="remove-url-btn"
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddUrl}
              className="add-url-btn"
              disabled={isSubmitting}
            >
              + Add Another URL
            </button>
          </div>
        </div>

        <div className="form-section">
          <label>Target Languages</label>
          <div className="languages-grid">
            {availableLanguages.map(lang => (
              <label key={lang.code} className="language-checkbox">
                <input
                  type="checkbox"
                  checked={languages.includes(lang.code)}
                  onChange={() => handleLanguageToggle(lang.code)}
                  disabled={isSubmitting}
                />
                <span>{lang.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="batch-summary">
          <h3>Summary</h3>
          <p>
            <strong>{urls.filter(u => u.trim()).length}</strong> URL(s) × 
            <strong> {languages.length}</strong> language(s) = 
            <strong> {urls.filter(u => u.trim()).length * (1 + languages.length)}</strong> total tasks
          </p>
          <small>
            ({urls.filter(u => u.trim()).length} scraping tasks + {urls.filter(u => u.trim()).length * languages.length} translation tasks)
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="cancel-btn"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Starting Batch...' : 'Start Batch Processing'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BatchWorkflow;
